export type WorkspaceTab = 'storyboard' | 'outline' | 'scenes' | 'studio' | 'narration' | 'blog' | 'lab';

export const ALL_WORKSPACE_TABS: readonly WorkspaceTab[] = ['storyboard', 'outline', 'scenes', 'studio', 'narration', 'lab', 'blog'];

export const WORKSPACE_TAB_LABELS: Record<WorkspaceTab, string> = {
  storyboard: 'Storyboard',
  outline: 'Outline',
  scenes: 'Scenes',
  studio: 'Studio',
  narration: 'Narration',
  lab: 'Lab',
  blog: 'Blog',
};

export interface TabVisibilityInput {
  creationMode?: string;
  hasOutline: boolean;
  hasLabAssets: boolean;
  hasStoryboard: boolean;
  showNarration: boolean;
  showBlog: boolean;
  /** Kubicle outlines keep their outline screen and labs whatever else exists. */
  keepsOwnScreens?: boolean;
  /** ?tab=outline: the Outline tab stays reachable next to the storyboard (rollback, V2-6 §3.7). */
  showOutline?: boolean;
  /** Every scene is built: Scenes has done its job and Studio holds the video. */
  videoReady?: boolean;
}

/** Kubicle projects (archetype slides or a Kubicle design system) keep today's screens. */
export function keepsOwnScreens(outline: { slides?: Array<Record<string, unknown>>; designSystemId?: unknown; templateId?: unknown } | null | undefined): boolean {
  if (!outline) return false;
  if ([outline.designSystemId, outline.templateId].some((v) => typeof v === 'string' && /kubicle/i.test(v))) return true;
  return (outline.slides ?? []).some((s) => typeof s.archetypeKey === 'string' && s.archetypeKey.length > 0);
}

export const isFootageMode = (creationMode?: string) => creationMode === 'footage';

export function visibleWorkspaceTabs(input: TabVisibilityInput): WorkspaceTab[] {
  if (isFootageMode(input.creationMode)) {
    return input.hasOutline ? ['storyboard', 'lab', 'studio'] : ['storyboard', 'lab'];
  }
  if (!input.hasOutline && !input.hasLabAssets && !input.hasStoryboard) return [];
  if (input.hasStoryboard && !input.keepsOwnScreens) {
    // The living storyboard (V2-6) is the review surface: it holds the outline (editable scene cards) and the labs.
    const tabs: WorkspaceTab[] = ['storyboard'];
    if (input.showOutline) tabs.push('outline');
    if (input.hasOutline) {
      if (!input.videoReady) tabs.push('scenes');
      tabs.push('studio');
      if (input.showNarration) tabs.push('narration');
    }
    if (input.hasLabAssets) tabs.push('lab');
    if (input.showBlog && input.hasOutline) tabs.push('blog');
    return tabs;
  }
  const tabs: WorkspaceTab[] = [];
  if (input.hasStoryboard) tabs.push('storyboard');
  tabs.push('outline');
  if (input.hasOutline) {
    tabs.push('studio');
    if (input.showNarration) tabs.push('narration');
  }
  tabs.push('lab');
  if (input.showBlog && input.hasOutline) tabs.push('blog');
  return tabs;
}

/** Scenes folds into Studio once the video is ready; every other tab stays put. */
export function foldedTab(active: WorkspaceTab, visible: WorkspaceTab[]): WorkspaceTab {
  return active === 'scenes' && !visible.includes('scenes') && visible.includes('studio') ? 'studio' : active;
}

export function defaultWorkspaceTab(creationMode?: string): WorkspaceTab {
  return isFootageMode(creationMode) ? 'storyboard' : 'outline';
}

export function resolveInitialTab(requested: WorkspaceTab | undefined, creationMode?: string): WorkspaceTab {
  if (!requested) return defaultWorkspaceTab(creationMode);
  if (requested === 'outline' && isFootageMode(creationMode)) return 'storyboard';
  return requested;
}

/** Where a finished video with a storyboard opens when the link names no tab (Direction B, beta only). */
export const FINISHED_VIDEO_OPENS: 'storyboard' | 'studio' = 'storyboard';

/** How long a video waits for its storyboard check and outline before it opens on what it has. */
export const LANDING_WAIT_MS = 6000;

export interface LandingFacts {
  creationMode?: string;
  hasStoryboard: boolean;
  hasOutline: boolean;
  keepsOwnScreens: boolean;
  /** The outline is complete or one of its scenes is built. */
  outlineDone: boolean;
  /** Every scene of the outline is built. */
  videoReady: boolean;
}

/**
 * Where a video opens when the link names no tab (Direction B, beta only), from facts known once the storyboard check and
 * the outline have both loaded, so the same video opens on the same tab whichever loads first. 'outline' means stay put.
 */
export function landingTab(f: LandingFacts, finished: 'storyboard' | 'studio' = FINISHED_VIDEO_OPENS): WorkspaceTab {
  if (isFootageMode(f.creationMode)) return f.hasStoryboard || f.hasOutline ? 'storyboard' : 'outline';
  if (f.hasStoryboard && !f.keepsOwnScreens) return finished === 'studio' && f.videoReady ? 'studio' : 'storyboard';
  return f.outlineDone ? 'studio' : 'outline';
}

export interface WorkspaceModeInput {
  outlineStatus?: string;
  /** Slide rows on this conversation (a video's scenes live on child conversations instead). */
  slideCount: number;
  planSlides?: Array<{ slideConversationId?: string; isDeleted?: boolean }>;
  collectionType?: string;
}

/**
 * Whether a video opens in outline review or in the studio. Built scenes (a plan slide with its own conversation)
 * open the studio whatever the plan's status: an agent or connector build can finish without approving the outline.
 */
export function workspaceMode({ outlineStatus, slideCount, planSlides = [], collectionType }: WorkspaceModeInput): 'video' | 'outline' {
  const builtScenes = planSlides.filter((s) => s.slideConversationId && !s.isDeleted).length;
  if (outlineStatus === 'complete' || outlineStatus === 'generating_slides' || outlineStatus === 'approved' || slideCount > 0 || builtScenes > 0) return 'video';
  if (outlineStatus === 'generating' || outlineStatus === 'draft') return 'outline';
  if (collectionType === 'VIDEO' && !outlineStatus) return 'outline';
  return 'video';
}
